// File: apps/app/src/core/loginUserThirdParty.ts

import { Capacitor } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import * as Sentry from '@sentry/solid'
import { initializeApp } from 'firebase/app'
import { getMessaging, getToken } from 'firebase/messaging'
import { client } from '../../utils/api-client'

// Prevent duplicate listener attachments/token fetches on resource refetches
let isPushInitialized = false

async function registerToken(token: string, platform: 'web' | 'ios' | 'android') {
	try {
		await client.registerPushToken({ token, platform })
		console.log(`✅ Push token registered for ${platform}`)
	} catch (err) {
		console.error(`❌ Failed to register push token for ${platform}:`, err)
	}
}

async function mobile(userId?: string, email?: string) {
	try {
		if (!Capacitor.isNativePlatform()) return

		Sentry.setUser({
			id: userId,
			email: email,
		})

		if (!isPushInitialized) {
			console.log('🔔 Initializing Capacitor Push Notifications')

			let permStatus = await PushNotifications.checkPermissions()

			if (permStatus.receive === 'prompt') {
				permStatus = await PushNotifications.requestPermissions()
			}

			if (permStatus.receive !== 'granted') {
				console.warn('⚠️ Push notification permission not granted')
				return
			}

			await PushNotifications.addListener('registration', (token) => {
				console.log('🔔 Push registration success, token:', token.value)
				registerToken(token.value, Capacitor.getPlatform() as 'ios' | 'android')
			})

			await PushNotifications.addListener('registrationError', (error) => {
				console.error('❌ Push registration error:', error.error)
			})

			await PushNotifications.register()
			isPushInitialized = true
		}
	} catch (err) {
		console.error('❌ initMobile third-party error:', err)
	}
}

async function web(userId?: string, email?: string) {
	if (userId) {
		if (email) {
			Sentry.setUser({
				id: userId,
				email: email,
			})
		}

		if (!isPushInitialized) {
			console.log('💻 Initializing Web FCM')
			try {
				const firebaseConfig = {
					apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
					authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
					projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
					storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
					messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
					appId: import.meta.env.VITE_FIREBASE_APP_ID,
				}

				const app = initializeApp(firebaseConfig)
				const messaging = getMessaging(app)

				let registration: ServiceWorkerRegistration | undefined

				if ('serviceWorker' in navigator) {
					const swParams = new URLSearchParams(firebaseConfig as any).toString()
					registration = await navigator.serviceWorker.register(
						`/firebase-messaging-sw.js?${swParams}`,
					)
					console.log('✅ Service worker registered manually for FCM')
				} else {
					console.warn('⚠️ Service workers are not supported in this browser.')
				}

				const token = await getToken(messaging, {
					vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
					serviceWorkerRegistration: registration,
				})

				if (token) {
					console.log('🔔 Web FCM token:', token)
					await registerToken(token, 'web')
					isPushInitialized = true
				} else {
					console.warn('⚠️ No Web FCM token available. Check permissions.')
				}
			} catch (err) {
				console.error('❌ initWeb FCM error:', err)
			}
		}
	}
}

export async function loginUserThirdParty(userId?: string, email?: string) {
	try {
		if (Capacitor.isNativePlatform()) {
			await mobile(userId, email)
		} else {
			await web(userId, email)
		}
	} catch (err) {
		console.error('❌ loginUserThirdParty error:', err)
	}
}
